import { Injectable, inject, signal, computed } from "@angular/core";
import { OfflineStorageService } from "../../offline/offline-storage.service";
import { InitialDownloadService } from "../../offline/initial-download.service";
import { TenantDateService } from "../../shared/services/tenant-date.service";
import type { LocalWorkOrder } from "../../offline/offline-storage.types";

/**
 * MyOrdersService — Manages the "My Orders" screen state.
 *
 * Reads from SQLite (offline-first). Pull-to-refresh triggers API download + SQLite update.
 * Separates orders into "Today" vs "Upcoming" by scheduledDate.
 */
@Injectable({ providedIn: "root" })
export class MyOrdersService {
  private readonly storage = inject(OfflineStorageService);
  private readonly download = inject(InitialDownloadService);
  private readonly tenantDate = inject(TenantDateService);

  /** All orders from SQLite */
  readonly orders = signal<LocalWorkOrder[]>([]);

  /**
   * Nombre de la plantilla de ejecución pre-seleccionada por el coordinador al
   * crear la OT, indexado por workOrderId. Solo se puebla para órdenes que traen
   * `executionTemplateId` (nivel 0 de la cadena) y cuya plantilla ya está en la
   * caché SQLite. Si la plantilla no está cacheada, la orden no aparece en el
   * mapa y la card no muestra nada de plantilla (opción A acordada).
   */
  readonly templateNames = signal<Record<string, string>>({});

  /** Loading state */
  readonly loading = signal(false);

  /** Last sync timestamp */
  readonly lastSync = computed(() => this.download.lastSyncAt());

  /** IDs of newly assigned orders (not yet seen) */
  readonly newAssignmentIds = signal<string[]>([]);

  /** Count of orders completed today (persists across refreshes) */
  readonly completedTodayCount = signal(0);

  /** Total orders assigned for today at session start (active + completed = stable denominator) */
  private initialTodayTotal = 0;

  /** Active filter: 'all' | 'ASSIGNED' | 'IN_PROGRESS' */
  readonly activeFilter = signal<string>("all");

  /** Count of orders per filter (for badge display in chips) */
  readonly filterCounts = computed(() => {
    const all = this.orders();
    const today = this.tenantDate.today();

    // Apply same date logic as filteredOrders
    const relevant = all.filter((o) => {
      if (!o.scheduledDate) return true;
      if (o.scheduledDate.startsWith(today)) return true;
      if (o.status === "IN_PROGRESS") return true;
      if (o.scheduledDate < today) return true;
      return false;
    });

    return {
      all: relevant.length,
      ASSIGNED: relevant.filter((o) => o.status === "ASSIGNED").length,
      IN_PROGRESS: relevant.filter((o) => o.status === "IN_PROGRESS").length,
    };
  });

  /** Orders filtered by active tab + date logic:
   * - Shows today's orders (ASSIGNED or IN_PROGRESS only)
   * - Shows IN_PROGRESS orders from any date (pending completion)
   * - Shows orders without scheduledDate (unscheduled — should always be visible)
   * - Shows ASSIGNED orders from past days (overdue — displayed with alert)
   * - Hides COMPLETED and CANCELLED orders (those belong in Historial tab)
   * - Hides only ASSIGNED orders from future days
   */
  readonly filteredOrders = computed(() => {
    const filter = this.activeFilter();
    const all = this.orders();
    const today = this.tenantDate.today();

    // Exclude terminal statuses — completed/cancelled go to "Historial" only
    const active = all.filter(
      (o) => o.status !== "COMPLETED" && o.status !== "CANCELLED",
    );

    // Date logic: show everything except ASSIGNED orders scheduled for future days
    const relevant = active.filter((o) => {
      if (!o.scheduledDate) return true; // Unscheduled — always show
      if (o.scheduledDate.startsWith(today)) return true; // Today — always show
      if (o.status === "IN_PROGRESS") return true; // In progress — always show
      // Past date + ASSIGNED = overdue — show with alert
      if (o.scheduledDate < today) return true;
      // Future ASSIGNED — hide (not relevant for today)
      return false;
    });

    if (filter === "all") return this.sortByTime(relevant);
    return this.sortByTime(relevant.filter((o) => o.status === filter));
  });

  /** Sort orders by scheduledTime ascending (earliest first, null/empty at end) */
  private sortByTime(orders: LocalWorkOrder[]): LocalWorkOrder[] {
    return [...orders].sort((a, b) => {
      const timeA = a.scheduledTime || "99:99";
      const timeB = b.scheduledTime || "99:99";
      return timeA.localeCompare(timeB);
    });
  }

  /** Orders that are overdue (scheduled for a past date and not completed) */
  readonly overdueOrderIds = computed(() => {
    const all = this.orders();
    const today = this.tenantDate.today();
    return new Set(
      all
        .filter(
          (o) =>
            o.scheduledDate &&
            o.scheduledDate < today &&
            o.status !== "IN_PROGRESS",
        )
        .map((o) => o.workOrderId),
    );
  });

  /** Today's orders (sorted by time ascending) */
  readonly todayOrders = computed(() => {
    const today = this.tenantDate.today();
    return this.sortByTime(
      this.filteredOrders().filter((o) => o.scheduledDate.startsWith(today)),
    );
  });

  /** Upcoming orders (sorted by date then time ascending) */
  readonly upcomingOrders = computed(() => {
    const today = this.tenantDate.today();
    return this.sortByTime(
      this.filteredOrders().filter((o) => !o.scheduledDate.startsWith(today)),
    );
  });

  /** Future ASSIGNED orders (scheduled for days after today — not shown in main list) */
  readonly futureOrders = computed(() => {
    const all = this.orders();
    const today = this.tenantDate.today();
    return all
      .filter(
        (o) =>
          o.status === "ASSIGNED" &&
          o.scheduledDate &&
          o.scheduledDate > today &&
          !o.scheduledDate.startsWith(today),
      )
      .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
  });

  /** Load orders from SQLite */
  async loadFromStorage(): Promise<void> {
    this.loading.set(true);
    try {
      const all = await this.storage.getWorkOrders({
        status: ["ASSIGNED", "IN_PROGRESS"],
      });
      console.info(
        "[MyOrdersService] loadFromStorage:",
        all.map((o) => ({ id: o.workOrderId.slice(-6), status: o.status })),
      );
      this.orders.set(all);
      void this.loadTemplateNames(all);

      // Track new assignments
      const newIds = all.filter((o) => o.isNew === 1).map((o) => o.workOrderId);
      this.newAssignmentIds.set(newIds);
    } catch (err) {
      // SQLite may fail on web — log and continue without clearing existing state.
      // If orders() is already populated by refresh(), don't overwrite with empty.
      console.warn("[MyOrdersService] Failed to load from storage:", err);
      if (this.orders().length === 0) {
        this.orders.set([]);
      }
    } finally {
      this.loading.set(false);
    }
  }

  /** Pull-to-refresh: download from API then reload from SQLite */
  async refresh(force = false): Promise<void> {
    this.loading.set(true);
    try {
      const downloadedOrders = await this.download.downloadAll(force);

      // downloadAll() returns the orders from the server.
      // If it returns an array (even empty), that's the truth — use it directly.
      // Filter out terminal statuses — technician's main view only shows active orders.
      const activeOrders = downloadedOrders.filter(
        (o) => o.status !== "COMPLETED" && o.status !== "CANCELLED",
      );

      // Count completed orders: orders that were in our initial set but are no longer active
      const today = this.tenantDate.today();
      const todayActive = activeOrders.filter((o) => {
        if (!o.scheduledDate) return true;
        if (o.scheduledDate.startsWith(today)) return true;
        if (o.status === "IN_PROGRESS") return true;
        if (o.scheduledDate < today) return true;
        return false;
      });

      // Set initial total on first load (the "full day" count)
      if (this.initialTodayTotal === 0 && todayActive.length > 0) {
        this.initialTodayTotal =
          todayActive.length + this.completedTodayCount();
      } else if (this.initialTodayTotal > 0) {
        // Adjust initial total downward if orders were removed (reassigned)
        // The total should be: current active + already completed
        const expectedTotal = todayActive.length + this.completedTodayCount();
        if (expectedTotal < this.initialTodayTotal) {
          this.initialTodayTotal = expectedTotal;
        }
      }

      // If the initial total is larger than current active, the difference = completed
      if (this.initialTodayTotal > 0) {
        const completed = Math.max(
          0,
          this.initialTodayTotal - todayActive.length,
        );
        this.completedTodayCount.set(completed);
      }

      this.orders.set(activeOrders);
      void this.loadTemplateNames(activeOrders);
      const newIds = activeOrders
        .filter((o) => o.isNew === 1)
        .map((o) => o.workOrderId);
      this.newAssignmentIds.set(newIds);
    } catch (err) {
      // Download failed (network error, etc.) — preserve existing in-memory data.
      // Try loading from SQLite as fallback only if we have no data in memory.
      console.warn(
        "[MyOrdersService] Refresh failed, keeping existing data:",
        err,
      );
      if (this.orders().length === 0) {
        await this.loadFromStorage();
      }
    } finally {
      this.loading.set(false);
    }
  }

  /** Lightweight poll: fetch only work orders (no config/templates/assets) */
  async refreshOrdersOnly(): Promise<void> {
    try {
      const orders = await this.download.fetchOrdersOnly();
      if (orders === null) return; // fetch failed — keep current data

      const activeOrders = orders.filter(
        (o) => o.status !== "COMPLETED" && o.status !== "CANCELLED",
      );
      this.orders.set(activeOrders);
      void this.loadTemplateNames(activeOrders);
    } catch {
      // Silently fail — polling is best-effort
    }
  }

  /**
   * Puebla `templateNames` con el nombre de la plantilla pre-seleccionada por el
   * coordinador (OT.executionTemplateId), leyendo SOLO la caché local de SQLite.
   *
   * Opción A acordada: si la orden no tiene `executionTemplateId`, o la plantilla
   * no está cacheada (sincronización parcial), la orden se omite del mapa y la
   * card no muestra nada de plantilla. No se hace ninguna llamada de red.
   */
  private async loadTemplateNames(orders: LocalWorkOrder[]): Promise<void> {
    try {
      const withTemplate = orders.filter((o) => o.executionTemplateId);
      if (withTemplate.length === 0) {
        this.templateNames.set({});
        return;
      }

      // Dedupe template ids to avoid redundant SQLite reads for orders that
      // share the same pre-selected template.
      const uniqueIds = [
        ...new Set(withTemplate.map((o) => o.executionTemplateId as string)),
      ];
      const entries = await Promise.all(
        uniqueIds.map(async (id) => {
          const tpl = await this.storage.getExecutionTemplate(id);
          return [id, tpl?.name ?? null] as const;
        }),
      );
      const nameById = new Map(entries);

      const map: Record<string, string> = {};
      for (const o of withTemplate) {
        const name = nameById.get(o.executionTemplateId as string);
        if (name) {
          map[o.workOrderId] = name;
        }
      }
      this.templateNames.set(map);
    } catch (err) {
      // SQLite unavailable (e.g. web) — degrade gracefully: no template labels.
      console.warn("[MyOrdersService] Failed to load template names:", err);
      this.templateNames.set({});
    }
  }

  /** Mark an order as "seen" (remove from new assignments) */
  markAsSeen(workOrderId: string): void {
    const current = this.newAssignmentIds();
    this.newAssignmentIds.set(current.filter((id) => id !== workOrderId));
  }

  /** Set the active filter tab */
  setFilter(filter: string): void {
    this.activeFilter.set(filter);
  }

  /** Update order status in-memory (for optimistic UI updates when offline) */
  updateOrderStatus(
    workOrderId: string,
    status: LocalWorkOrder["status"],
  ): void {
    // Track completions for the day progress bar
    if (status === "COMPLETED") {
      this.completedTodayCount.update((c) => c + 1);
    }
    this.orders.update((prev) =>
      prev.map((o) => (o.workOrderId === workOrderId ? { ...o, status } : o)),
    );
  }

  /** Update order fields in-memory (for optimistic UI after linking asset, etc.) */
  patchOrder(workOrderId: string, patch: Partial<LocalWorkOrder>): void {
    this.orders.update((prev) =>
      prev.map((o) => (o.workOrderId === workOrderId ? { ...o, ...patch } : o)),
    );
  }
}
